/**
 * Language Service
 * Fetches and caches language configurations from the backend
 * Provides the data source for the validation engine
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// In-memory cache for language configurations
const languageCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

/**
 * Fetch language configuration from backend with caching
 */
export const getLanguageConfig = async (languageId) => {
  const cacheKey = `config-${languageId}`;
  const cached = languageCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/languages/${languageId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch language config: ${response.status}`);
    }
    
    const data = await response.json();
    languageCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error(`Error fetching language config for ${languageId}:`, error);
    // Return cached data if available, even if expired
    if (cached) return cached.data;
    throw error;
  }
};

/**
 * Get all available languages
 */
export const getAvailableLanguages = async () => {
  const cacheKey = 'all-languages';
  const cached = languageCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/languages`);
    if (!response.ok) {
      throw new Error(`Failed to fetch languages: ${response.status}`);
    }
    
    const data = await response.json();
    languageCache.set(cacheKey, { data: data.languages, timestamp: Date.now() });
    return data.languages;
  } catch (error) {
    console.error('Error fetching available languages:', error);
    if (cached) return cached.data;
    throw error;
  }
};

/**
 * Get functions for a specific language
 */
export const getLanguageFunctions = async (languageId, category = null) => {
  const cacheKey = `functions-${languageId}-${category || 'all'}`;
  const cached = languageCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  try {
    let url = `${API_URL}/api/languages/${languageId}/functions`;
    if (category) {
      url += `?category=${category}`;
    }
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch functions: ${response.status}`);
    }
    
    const data = await response.json();
    languageCache.set(cacheKey, { data: data.functions, timestamp: Date.now() });
    return data.functions;
  } catch (error) {
    console.error(`Error fetching functions for ${languageId}:`, error);
    if (cached) return cached.data;
    throw error;
  }
};

/**
 * Clear the language cache (useful when admin updates configs)
 */
export const clearLanguageCache = () => {
  languageCache.clear();
};

/**
 * Preload language configurations for common languages
 */
export const preloadLanguageConfigs = async (languages = ['c', 'cpp', 'python', 'java', 'javascript']) => {
  const promises = languages.map(lang => getLanguageConfig(lang).catch(() => null));
  await Promise.all(promises);
};

export default {
  getLanguageConfig,
  getAvailableLanguages,
  getLanguageFunctions,
  clearLanguageCache,
  preloadLanguageConfigs,
};
